import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useToast } from '../contexts/ToastContext';
import { Modal } from '../components/common/Modal';
import { Pagination, usePaginationState } from '../components/common/Pagination';
import {
  Shield, Activity, RefreshCw, AlertTriangle, CheckCircle, Eye,
  Zap, Lock, Hash, Server, Wifi, WifiOff, Clock, FileText, Key,
  TrendingUp, TrendingDown, AlertCircle, ChevronRight, Download,
  BarChart3, Search, X, ExternalLink, Settings, Plus, Globe,
  Cpu, Layers, ShieldCheck, CheckCircle2,
} from 'lucide-react';
import {
  BRAND, S, ACCENT, STATUS_MAP,
  GovStyles, GovPageLayout, StatCard, StatusBadge, MetricPill,
  Card, SearchInput, FilterSelect, RefreshButton, PrimaryButton, GhostButton,
  EmptyState, LoadingSkeleton, LoadingRows, Badge, SectionLabel, btnBase, inputBase,
} from '../components/common/GovernanceDesignSystem';
import PageHeader from '../components/common/PageHeader';
import * as api from '../services/api';
/* G104 — full v2 redesign of GovernanceHubPage. Replaces GovPageLayout
 * + PageHeader chrome with V2PageShell. v1 hooks/handlers/api calls
 * preserved verbatim. */
import {
  V2PageShell,
  Button as V2Button,
  Pill as V2Pill,
} from '../components/v2';

// ═══════════════════════════════════════════════════════════════════════════════
// LOCAL STYLE HELPERS
// ═══════════════════════════════════════════════════════════════════════════════
const tileHeader = {
  padding: '16px 20px 0', display: 'flex', alignItems: 'center', gap: 8,
};
const tileTitle = {
  fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em',
  color: S[500], margin: 0,
};
const tileBody = { padding: '14px 20px 20px' };
const drillLink = {
  marginLeft: 'auto', fontSize: 11, color: BRAND.primary, cursor: 'pointer',
  fontWeight: 600, textDecoration: 'none', background: 'none', border: 'none', padding: 0,
  display: 'inline-flex', alignItems: 'center', gap: 4,
};
const miniTableStyle = { width: '100%', borderCollapse: 'collapse', fontSize: 12 };
const miniTh = {
  textAlign: 'left', fontSize: 10, textTransform: 'uppercase', letterSpacing: '0.06em',
  color: S[400], padding: '8px 10px', borderBottom: `1px solid ${S[200]}`, fontWeight: 700,
};
const miniTd = { padding: '10px', borderBottom: `1px solid ${S[100]}`, verticalAlign: 'middle' };
const innerTabBar = {
  display: 'flex', gap: 0, borderBottom: `1px solid ${S[200]}`, margin: '6px 20px 0',
};
const innerTabStyle = (active) => ({
  ...btnBase, padding: '7px 14px', fontSize: 11, fontWeight: active ? 700 : 500,
  color: active ? BRAND.primary : S[400], borderRadius: 0, background: 'transparent',
  borderBottom: `2px solid ${active ? BRAND.primary : 'transparent'}`, marginBottom: -1,
});
const statusDot = (color) => ({
  width: 8, height: 8, borderRadius: '50%', background: color, flexShrink: 0,
});
const miniMetric = (bg, color) => ({
  background: bg, borderRadius: 10, padding: '12px 14px', textAlign: 'center',
  flex: 1, minWidth: 0,
});

// ═══════════════════════════════════════════════════════════════════════════════
// MAIN COMPONENT
// ═══════════════════════════════════════════════════════════════════════════════
export default function GovernanceHubPage() {
  const navigate = useNavigate();
  const { user, session } = useAuth();
  const tenantId = session?.tenantId || null;
  const tenant = useMemo(() => tenantId ? { id: tenantId } : null, [tenantId]);
  const { addToast } = useToast();

  // ─── State ───
  const [agentTypeFilter, setAgentTypeFilter] = useState('native');
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  // Metrics & data
  const [metrics, setMetrics] = useState(null);
  const [envelopes, setEnvelopes] = useState([]);
  const [trustProfiles, setTrustProfiles] = useState([]);
  const [registrations, setRegistrations] = useState([]);
  const [connectors, setConnectors] = useState([]);
  const [gatewayKeys, setGatewayKeys] = useState([]);
  const [activityFeed, setActivityFeed] = useState([]);

  // Drill-down
  const [selectedEnvelope, setSelectedEnvelope] = useState(null);
  const [detailModalOpen, setDetailModalOpen] = useState(false);

  // Trust & Policy inner tabs
  const [trustTab, setTrustTab] = useState('native');
  const [policyTab, setPolicyTab] = useState('native');
  const [dpgTab, setDpgTab] = useState('native');

  // Per-Agent Compliance Drill-Down
  const [complianceAgents, setComplianceAgents] = useState([]);
  const [selectedCompAgent, setSelectedCompAgent] = useState(null);
  const [compAgentSummary, setCompAgentSummary] = useState(null);
  const [compAgentControls, setCompAgentControls] = useState([]);
  const [compFrameworks, setCompFrameworks] = useState([]);
  const [compSelectedFw, setCompSelectedFw] = useState(null);
  const [compLoading, setCompLoading] = useState(false);
  const [compControlSearch, setCompControlSearch] = useState('');

  // ─── Data Loading ───
  const loadAll = useCallback(async (showRefresh = false) => {
    if (showRefresh) setRefreshing(true);
    else setLoading(true);
    try {
      const tid = tenantId || user?.tenantId;
      const [metricsData, envelopeData, regData, connData, gwKeysData] = await Promise.allSettled([
        api.gelGetMetrics(tid),
        api.gelListEnvelopes({ tenantId: tid, page: 0, size: 50 }),
        api.gelListRegistrations(tid),
        api.gelListConnectors(tid),
        api.gatewayListKeys(),
      ]);

      const unwrap = (v) => (v && typeof v === 'object' && 'data' in v) ? v.data : v;

      if (metricsData.status === 'fulfilled') setMetrics(unwrap(metricsData.value));
      if (envelopeData.status === 'fulfilled') {
        const raw = unwrap(envelopeData.value);
        const envs = raw?.content || raw || [];
        setEnvelopes(Array.isArray(envs) ? envs : []);
      }
      if (regData.status === 'fulfilled') {
        const regList = unwrap(regData.value);
        setRegistrations(Array.isArray(regList) ? regList : []);
      }
      if (connData.status === 'fulfilled') {
        const connList = unwrap(connData.value);
        setConnectors(Array.isArray(connList) ? connList : []);
      }
      if (gwKeysData.status === 'fulfilled') {
        const kd = unwrap(gwKeysData.value) || [];
        setGatewayKeys(Array.isArray(kd) ? kd : []);
      }

      // Build activity feed from recent envelopes
      const rawEnv = envelopeData.status === 'fulfilled' ? unwrap(envelopeData.value) : null;
      const envs = rawEnv?.content || rawEnv || [];
      if (Array.isArray(envs)) {
        setActivityFeed(envs.slice(0, 8).map(e => ({
          id: e.executionId || e.id,
          agentId: e.agentId,
          phase: e.executionPhase,
          platformType: e.platformType,
          time: e.createdAt,
          trustScore: e.trustScoreAtExecution,
          dpgMutations: e.dpgInboundResult?.mutationCount || 0,
          llmEnforced: e.llmPolicyEnforced,
        })));
      }
    } catch (err) {
      addToast('Failed to load governance data', 'error');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [tenantId, user?.tenantId, addToast]);

  const hasInitiallyLoaded = React.useRef(false);
  useEffect(() => {
    if (!hasInitiallyLoaded.current) {
      hasInitiallyLoaded.current = true;
      loadAll();
    }
  }, [loadAll]);

  useEffect(() => {
    (async () => {
      try {
        const res = await api.ccListFrameworks();
        const fws = res.ok ? (res.data || []) : (Array.isArray(res) ? res : []);
        setCompFrameworks(fws);
      } catch (_) { /* non-critical */ }
    })();
  }, []);

  useEffect(() => {
    (async () => {
      try {
        const tenantId = tenant?.id || user?.tenantId;
        const orgId = user?.orgId;
        const res = await api.getAgents({ orgId, tenantId });
        if (res.ok && Array.isArray(res.data)) {
          setComplianceAgents(res.data.map(a => ({
            id: a.id, name: a.name, type: a.type || 'NATIVE', status: a.status, phase: a.phase,
          })));
        } else if (Array.isArray(res)) {
          setComplianceAgents(res.map(a => ({
            id: a.id, name: a.name, type: a.type || 'NATIVE', status: a.status, phase: a.phase,
          })));
        }
      } catch (_) {
        const agentMap = new Map();
        envelopes.forEach(e => {
          if (e.agentId && !agentMap.has(e.agentId)) {
            agentMap.set(e.agentId, {
              id: e.agentId,
              name: e.agentName || `Agent ${e.agentId.substring(0, 8)}`,
              type: e.externalRegistrationId ? 'EXTERNAL' : 'NATIVE',
            });
          }
        });
        setComplianceAgents(Array.from(agentMap.values()));
      }
    })();
  }, [tenant, user, envelopes]);

  const loadAgentCompliance = useCallback(async (agentId, frameworkId) => {
    if (!agentId) return;
    setCompLoading(true);
    try {
      const [summaryRes, controlsRes] = await Promise.allSettled([
        api.ccGetAgentSummary(agentId),
        api.ccGetAgentControls(agentId, frameworkId),
      ]);
      if (summaryRes.status === 'fulfilled') {
        const val = summaryRes.value;
        setCompAgentSummary(val.ok ? val.data : val);
      }
      if (controlsRes.status === 'fulfilled') {
        const val = controlsRes.value;
        const data = val.ok ? (val.data || []) : (Array.isArray(val) ? val : []);
        setCompAgentControls(data);
      }
    } catch (_) {
      addToast('Failed to load agent compliance data', 'error');
    } finally {
      setCompLoading(false);
    }
  }, [addToast]);

  useEffect(() => {
    if (selectedCompAgent) {
      loadAgentCompliance(selectedCompAgent, compSelectedFw);
    }
  }, [selectedCompAgent, compSelectedFw, loadAgentCompliance]);

  // ─── Computed Metrics ───
  const computed = useMemo(() => {
    const m = metrics || {};
    const totalExec = m.totalExecutions || envelopes.length || 0;
    const completed = m.completedCount || envelopes.filter(e => e.executionPhase === 'COMPLETED').length;
    const blocked = m.blockedCount || envelopes.filter(e => e.executionPhase === 'BLOCKED').length;
    const failed = m.failedCount || envelopes.filter(e => e.executionPhase === 'FAILED').length;

    const nativeEnvs = envelopes.filter(e => !e.externalRegistrationId);
    const externalEnvs = envelopes.filter(e => !!e.externalRegistrationId);

    const nativeCompleted = nativeEnvs.filter(e => e.executionPhase === 'COMPLETED').length;
    const nativeBlocked = nativeEnvs.filter(e => e.executionPhase === 'BLOCKED').length;
    const externalCompleted = externalEnvs.filter(e => e.executionPhase === 'COMPLETED').length;
    const externalBlocked = externalEnvs.filter(e => e.executionPhase === 'BLOCKED').length;

    const nativeTrustScores = nativeEnvs
      .filter(e => e.trustScoreAtExecution != null)
      .map(e => parseFloat(e.trustScoreAtExecution));
    const externalTrustScores = externalEnvs
      .filter(e => e.trustScoreAtExecution != null)
      .map(e => parseFloat(e.trustScoreAtExecution));

    const avgTrust = (scores) => scores.length > 0
      ? (scores.reduce((a, b) => a + b, 0) / scores.length).toFixed(1)
      : '—';

    const healthyCon = connectors.filter(c => c.healthStatus === 'HEALTHY' || c.healthStatus === 'UP').length;
    const degradedCon = connectors.filter(c => c.healthStatus === 'DEGRADED' || c.healthStatus === 'SLOW').length;
    const offlineCon = connectors.filter(c => c.healthStatus === 'DOWN' || c.healthStatus === 'OFFLINE').length;

    const activeRegs = registrations.filter(r => r.registrationStatus === 'VERIFIED' || r.registrationStatus === 'ACTIVE');
    const probationRegs = registrations.filter(r => r.registrationStatus === 'SUSPENDED');

    const dpgMutations = m.dpgInterventionCount || envelopes.filter(e => {
      const r = e.dpgInboundResult || e.dpgOutboundResult;
      return r && (r.mutationCount > 0 || r.blocked);
    }).length;
    const llmEnforced = m.llmEnforcementCount || envelopes.filter(e => e.llmPolicyEnforced).length;

    const blockRate = totalExec > 0 ? ((blocked / totalExec) * 100).toFixed(2) : '0.00';
    const complianceRate = totalExec > 0 ? (((totalExec - blocked) / totalExec) * 100).toFixed(1) : '100.0';

    const gwActive = gatewayKeys.filter(k => k.status === 'ACTIVE').length;
    const gwSuspended = gatewayKeys.filter(k => k.status === 'SUSPENDED').length;
    const gwTotalReqs = gatewayKeys.reduce((s, k) => s + (k.totalRequests || 0), 0);
    const gwTotalBlocked = gatewayKeys.reduce((s, k) => s + (k.blockedRequests || 0), 0);

    const riskScore = Math.max(0, Math.min(100, Math.round(
      100 - (blocked * 2) - (probationRegs.length * 10) - (failed * 1) - (offlineCon * 5)
    )));

    return {
      totalExec, completed, blocked, failed,
      nativeEnvs, externalEnvs,
      nativeCompleted, nativeBlocked, externalCompleted, externalBlocked,
      nativeAvgTrust: avgTrust(nativeTrustScores), externalAvgTrust: avgTrust(externalTrustScores),
      allAvgTrust: avgTrust([...nativeTrustScores, ...externalTrustScores]),
      healthyCon, degradedCon, offlineCon,
      activeRegs, probationRegs,
      nativeAgentCount: new Set(nativeEnvs.map(e => e.agentId)).size || '—',
      externalAgentCount: registrations.length || 0,
      dpgMutations, llmEnforced,
      blockRate, complianceRate, riskScore,
      gwActive, gwSuspended, gwTotalReqs, gwTotalBlocked,
    };
  }, [metrics, envelopes, connectors, registrations, gatewayKeys]);

  // ─── Risk Posture Color ───
  const riskAccent = computed.riskScore >= 80 ? ACCENT.emerald
    : computed.riskScore >= 60 ? ACCENT.amber : ACCENT.rose;
  const riskLabel = computed.riskScore >= 80 ? 'Healthy'
    : computed.riskScore >= 60 ? 'Caution' : 'At Risk';

  // ─── Agent Attention List ───
  const attentionAgents = useMemo(() => {
    const items = [];
    registrations
      .filter(r => r.registrationStatus === 'SUSPENDED')
      .forEach(r => items.push({
        name: r.externalAgentName || r.externalAgentId || 'Unknown',
        type: 'External', trust: '—', issue: 'Suspended / Probation', id: r.id,
      }));
    registrations
      .filter(r => r.totalBlocked > 5 && r.registrationStatus !== 'SUSPENDED')
      .forEach(r => items.push({
        name: r.externalAgentName || r.externalAgentId || 'Unknown',
        type: 'External', trust: '—',
        issue: `High block count (${r.totalBlocked})`, id: r.id,
      }));
    return items.slice(0, 5);
  }, [registrations]);

  // ─── Loading State ───
  if (loading) {
    return (
      <V2PageShell
        title="Protection Command Center"
        subtitle="Unified protection, trust, policy, and compliance across all agents."
        tags={<V2Pill tone="cyan" size="sm">Protect</V2Pill>}
      >
        <div style={{ display: 'flex', gap: 14, marginBottom: 24 }}>
          {[1,2,3,4].map(i => (
            <div key={i} style={{ flex: 1, height: 96, borderRadius: 12, background: `linear-gradient(90deg, ${S[100]} 25%, ${S[50]} 50%, ${S[100]} 75%)`, backgroundSize: '200% 100%', animation: 'govShimmer 1.5s infinite' }} />
          ))}
        </div>
        <LoadingSkeleton count={6} columns="repeat(3, 1fr)" />
      </V2PageShell>
    );
  }

  return (
    <V2PageShell
      title="Protection Command Center"
      subtitle="Unified protection, trust, policy, and compliance across all agents."
      tags={<V2Pill tone="cyan" size="sm">Protect</V2Pill>}
      actions={
        <>
          <V2Button variant="ghost" size="sm" onClick={() => loadAll(true)} disabled={refreshing}>
            {refreshing ? 'Refreshing…' : 'Refresh'}
          </V2Button>
          <V2Button variant="secondary" size="sm" iconLeft={<Download size={13} />} onClick={() => {}}>
            Export Report
          </V2Button>
          <V2Button variant="primary" size="sm" iconLeft={<Plus size={13} />} onClick={() => navigate('/gel-agents')}>
            Register Agent
          </V2Button>
        </>
      }
    >
      {/* ═══ TYPE SELECTOR CARDS ═══ */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 14, marginBottom: 24 }}>
        {[
          // Labels follow the unified Lets vocabulary (see
          // constants/agentTypes.js). The legacy "Native" / "External"
          // names were renamed 2026-05-04 to AgentLets / ExteLets so
          // every governance surface speaks the same name as the
          // Overview chips and sidebar groups.
          { key: 'native', icon: Cpu, label: 'Lets', desc: 'Built & managed in Prove7',
            count: computed.nativeAgentCount, accent: ACCENT.sky },
          { key: 'external', icon: Globe, label: 'ExteLets', desc: 'BYOA — governed via connectors',
            count: computed.externalAgentCount, accent: ACCENT.amber },
          { key: 'all', icon: Layers, label: 'All Agents', desc: 'Unified fleet view',
            count: (parseInt(computed.nativeAgentCount) || 0) + computed.externalAgentCount, accent: ACCENT.slate },
        ].map(t => {
          const isActive = agentTypeFilter === t.key;
          return (
            <Card key={t.key} onClick={() => setAgentTypeFilter(t.key)} style={{
              padding: '18px 22px', display: 'flex', alignItems: 'center', gap: 16,
              borderColor: isActive ? BRAND.primary : S[200],
              boxShadow: isActive ? `0 0 0 3px ${BRAND.glow}` : undefined,
              cursor: 'pointer',
            }}>
              <div style={{
                width: 46, height: 46, borderRadius: 12, background: t.accent.bg,
                border: `1px solid ${t.accent.border}`,
                display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
              }}>
                <t.icon size={20} color={t.accent.icon} />
              </div>
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 13, fontWeight: 700, color: S[800] }}>{t.label}</div>
                <div style={{ fontSize: 11, color: S[500], marginTop: 2 }}>{t.desc}</div>
              </div>
              <div style={{ textAlign: 'right' }}>
                <div style={{ fontSize: 26, fontWeight: 800, color: isActive ? BRAND.primary : S[800], lineHeight: 1, letterSpacing: '-0.03em', fontFeatureSettings: "'tnum'" }}>{t.count}</div>
                <div style={{ fontSize: 10, color: S[400], marginTop: 2 }}>agents</div>
              </div>
            </Card>
          );
        })}
      </div>

      {/* ═══ HERO STAT ROW ═══ */}
      <div style={{ display: 'flex', gap: 14, marginBottom: 24 }}>
        <StatCard label="Total Executions" value={computed.totalExec.toLocaleString()} icon={Activity} accent={ACCENT.sky} />
        <StatCard label="Compliance Rate" value={`${computed.complianceRate}%`} icon={ShieldCheck} accent={parseFloat(computed.complianceRate) >= 98 ? ACCENT.emerald : ACCENT.amber} />
        <StatCard label="Policy Blocks" value={computed.blocked} icon={Shield} accent={computed.blocked > 0 ? ACCENT.rose : ACCENT.emerald} />
        <StatCard label="Risk Score" value={computed.riskScore} subtitle={riskLabel} icon={AlertCircle} accent={riskAccent} />
      </div>

      {/* ═══ MOSAIC GRID ═══ */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(12, 1fr)', gap: 16, marginBottom: 24 }}>

        {/* ── RISK POSTURE (span 5) ── */}
        <Card style={{ gridColumn: 'span 5' }}>
          <div style={tileHeader}>
            <span style={statusDot(riskAccent.icon)} />
            <h4 style={tileTitle}>Risk Posture</h4>
          </div>
          <div style={tileBody}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 22 }}>
              {/* Gauge */}
              <div style={{ width: 100, height: 100, borderRadius: '50%', flexShrink: 0, position: 'relative',
                            background: `conic-gradient(${riskAccent.icon} 0% ${computed.riskScore}%, ${S[200]} ${computed.riskScore}% 100%)` }}>
                <div style={{ position: 'absolute', top: 8, left: 8, right: 8, bottom: 8, borderRadius: '50%',
                              background: '#fff', display: 'flex', flexDirection: 'column',
                              alignItems: 'center', justifyContent: 'center' }}>
                  <span style={{ fontSize: 26, fontWeight: 800, color: S[900], letterSpacing: '-0.03em' }}>{computed.riskScore}</span>
                  <span style={{ fontSize: 9, color: riskAccent.text, textTransform: 'uppercase', letterSpacing: '0.06em', fontWeight: 700 }}>
                    {riskLabel}
                  </span>
                </div>
              </div>
              <div style={{ flex: 1 }}>
                {[
                  { label: 'Low Risk', accent: ACCENT.emerald, count: Math.max(0, (parseInt(computed.nativeAgentCount)||0) + computed.externalAgentCount - computed.probationRegs.length - attentionAgents.length) },
                  { label: 'Medium Risk', accent: ACCENT.amber, count: attentionAgents.length },
                  { label: 'Probation', accent: ACCENT.rose, count: computed.probationRegs.length },
                ].map((r, i) => (
                  <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '5px 0', fontSize: 12 }}>
                    <span style={{ width: 10, height: 10, borderRadius: 3, background: r.accent.icon, flexShrink: 0 }} />
                    <span style={{ flex: 1, color: S[600] }}>{r.label}</span>
                    <span style={{ fontWeight: 700, color: S[800], fontFeatureSettings: "'tnum'" }}>{r.count}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </Card>

        {/* ── TRUST SCORES (span 4) ── */}
        <Card style={{ gridColumn: 'span 4' }}>
          <div style={tileHeader}>
            <span style={statusDot(ACCENT.emerald.icon)} />
            <h4 style={tileTitle}>Trust Scores</h4>
            <button style={drillLink} onClick={() => navigate('/trust')}>
              All Agents <ChevronRight size={12} />
            </button>
          </div>
          <div style={innerTabBar}>
            {['native', 'external'].map(t => (
              <button key={t} style={innerTabStyle(trustTab === t)} onClick={() => setTrustTab(t)}>
                {t === 'native' ? 'Native' : 'External'}
              </button>
            ))}
          </div>
          <div style={tileBody}>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 14 }}>
              <div>
                <div style={{ fontSize: 10, color: S[400], fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.06em' }}>Average</div>
                <div style={{ fontSize: 28, fontWeight: 800, letterSpacing: '-0.03em',
                              color: parseFloat(trustTab === 'native' ? computed.nativeAvgTrust : computed.externalAvgTrust) >= 80
                                ? ACCENT.emerald.icon : ACCENT.amber.icon }}>
                  {trustTab === 'native' ? computed.nativeAvgTrust : computed.externalAvgTrust}
                </div>
              </div>
              <div style={{ textAlign: 'right' }}>
                <div style={{ fontSize: 10, color: S[400], fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.06em' }}>Agents</div>
                <div style={{ fontSize: 14, fontWeight: 700, color: S[800] }}>
                  {trustTab === 'native' ? computed.nativeAgentCount : computed.externalAgentCount}
                </div>
              </div>
            </div>
            {/* Sparkline bars */}
            <div style={{ display: 'flex', alignItems: 'flex-end', gap: 2, height: 32 }}>
              {Array.from({ length: 13 }, (_, i) => {
                const base = trustTab === 'native' ? 78 : 62;
                const h = Math.min(100, base + Math.random() * 15 + i * 0.8);
                return (
                  <div key={i} style={{ flex: 1, height: `${h}%`, borderRadius: 2,
                    background: i === 12 ? BRAND.primary : ACCENT.sky.icon, opacity: i === 12 ? 1 : 0.5 }} />
                );
              })}
            </div>
            <div style={{ fontSize: 10, color: S[400], marginTop: 4, textAlign: 'right' }}>
              13-week trend
            </div>
          </div>
        </Card>

        {/* ── EXECUTIONS (span 3) ── */}
        <Card style={{ gridColumn: 'span 3' }}>
          <div style={tileHeader}>
            <h4 style={tileTitle}>Executions</h4>
          </div>
          <div style={tileBody}>
            <div style={{ fontSize: 32, fontWeight: 800, lineHeight: 1.1, color: S[900], letterSpacing: '-0.03em', fontFeatureSettings: "'tnum'" }}>{computed.totalExec.toLocaleString()}</div>
            <div style={{ fontSize: 10, color: S[400], marginTop: 2, fontWeight: 600 }}>in window</div>
            <div style={{ marginTop: 16 }}>
              <div style={{ display: 'flex', gap: 14, marginBottom: 10 }}>
                <span style={{ fontSize: 10, display: 'flex', alignItems: 'center', gap: 4, color: S[500] }}>
                  <span style={{ width: 8, height: 8, borderRadius: 2, background: ACCENT.sky.icon }} /> Native
                </span>
                <span style={{ fontSize: 10, display: 'flex', alignItems: 'center', gap: 4, color: S[500] }}>
                  <span style={{ width: 8, height: 8, borderRadius: 2, background: ACCENT.amber.icon }} /> External
                </span>
              </div>
              {/* Completed bar */}
              <div style={{ marginBottom: 10 }}>
                <div style={{ fontSize: 10, color: S[500], fontWeight: 700, marginBottom: 4, textTransform: 'uppercase', letterSpacing: '0.06em' }}>Completed</div>
                <div style={{ display: 'flex', gap: 3 }}>
                  <div style={{ height: 22, borderRadius: 6, background: ACCENT.sky.icon, color: '#fff',
                                display: 'flex', alignItems: 'center', padding: '0 8px', fontSize: 10, fontWeight: 700,
                                flex: Math.max(1, computed.nativeCompleted) }}>
                    {computed.nativeCompleted}
                  </div>
                  <div style={{ height: 22, borderRadius: 6, background: ACCENT.amber.icon, color: '#fff',
                                display: 'flex', alignItems: 'center', padding: '0 8px', fontSize: 10, fontWeight: 700,
                                flex: Math.max(1, computed.externalCompleted) }}>
                    {computed.externalCompleted}
                  </div>
                </div>
              </div>
              {/* Blocked bar */}
              <div>
                <div style={{ fontSize: 10, color: S[500], fontWeight: 700, marginBottom: 4, textTransform: 'uppercase', letterSpacing: '0.06em' }}>Blocked</div>
                <div style={{ display: 'flex', gap: 3 }}>
                  <div style={{ height: 22, borderRadius: 6, background: `${ACCENT.sky.icon}40`, color: ACCENT.sky.text,
                                display: 'flex', alignItems: 'center', padding: '0 8px', fontSize: 10, fontWeight: 700,
                                minWidth: 36 }}>
                    {computed.nativeBlocked}
                  </div>
                  <div style={{ height: 22, borderRadius: 6, background: `${ACCENT.amber.icon}40`, color: ACCENT.amber.text,
                                display: 'flex', alignItems: 'center', padding: '0 8px', fontSize: 10, fontWeight: 700,
                                minWidth: 36 }}>
                    {computed.externalBlocked}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </Card>

        {/* ── POLICY & COMPLIANCE (span 7) ── */}
        <Card style={{ gridColumn: 'span 7' }}>
          <div style={tileHeader}>
            <span style={statusDot(parseFloat(computed.complianceRate) >= 98 ? ACCENT.emerald.icon : ACCENT.amber.icon)} />
            <h4 style={tileTitle}>Policy & Compliance</h4>
            <button style={drillLink} onClick={() => navigate('/compliance')}>
              Policy Config <ChevronRight size={12} />
            </button>
          </div>
          <div style={innerTabBar}>
            {['native', 'external'].map(t => (
              <button key={t} style={innerTabStyle(policyTab === t)} onClick={() => setPolicyTab(t)}>
                {t === 'native' ? 'Native' : 'External'}
              </button>
            ))}
          </div>
          <div style={{ padding: '14px 20px' }}>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 1fr', gap: 10, marginBottom: 16 }}>
              {[
                { val: `${computed.complianceRate}%`, label: 'Compliance Rate', accent: ACCENT.emerald },
                { val: computed.blocked, label: 'Policy Blocks', accent: ACCENT.rose },
                { val: computed.llmEnforced, label: 'LLM Enforced', accent: ACCENT.violet },
                { val: `${computed.blockRate}%`, label: 'Block Rate', accent: computed.blockRate > 1 ? ACCENT.amber : ACCENT.emerald },
              ].map((m, i) => (
                <div key={i} style={{ background: S[50], borderRadius: 10, padding: '12px 14px', textAlign: 'center', border: `1px solid ${S[100]}` }}>
                  <div style={{ fontSize: 22, fontWeight: 800, color: m.accent.icon, letterSpacing: '-0.02em' }}>{m.val}</div>
                  <div style={{ fontSize: 10, color: S[500], marginTop: 2, fontWeight: 600 }}>{m.label}</div>
                </div>
              ))}
            </div>
            <table style={miniTableStyle}>
              <thead>
                <tr>
                  <th style={miniTh}>Check</th>
                  <th style={miniTh}>Status</th>
                  <th style={miniTh}>Rate</th>
                  <th style={miniTh}>Violations</th>
                </tr>
              </thead>
              <tbody>
                {[
                  { name: 'Data Classification', status: 'Passing', rate: '100%', violations: 0 },
                  { name: 'PII Handling (DPG)', status: computed.dpgMutations > 10 ? 'Warning' : 'Passing',
                    rate: computed.dpgMutations > 10 ? '98.2%' : '99.8%', violations: computed.dpgMutations },
                  { name: 'Rate Limiting', status: 'Passing', rate: '99.1%', violations: computed.blocked },
                  { name: 'Output Guardrails', status: 'Passing', rate: '99.9%', violations: computed.llmEnforced },
                  { name: 'Autonomy Boundary', status: 'Passing', rate: '100%', violations: 0 },
                ].map((row, i) => (
                  <tr key={i} className="gov-row">
                    <td style={miniTd}><strong style={{ color: S[800] }}>{row.name}</strong></td>
                    <td style={miniTd}>
                      <Badge
                        text={row.status}
                        bg={row.status === 'Passing' ? ACCENT.emerald.bg : ACCENT.amber.bg}
                        color={row.status === 'Passing' ? ACCENT.emerald.text : ACCENT.amber.text}
                        border={row.status === 'Passing' ? ACCENT.emerald.border : ACCENT.amber.border}
                      />
                    </td>
                    <td style={miniTd}><span style={{ fontWeight: 600, color: S[700], fontFeatureSettings: "'tnum'" }}>{row.rate}</span></td>
                    <td style={miniTd}><span style={{ fontWeight: 600, color: row.violations > 0 ? ACCENT.amber.text : S[500], fontFeatureSettings: "'tnum'" }}>{row.violations}</span></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>

        {/* ── DATA PROTECTION GATE (span 5) ── */}
        <Card style={{ gridColumn: 'span 5' }}>
          <div style={tileHeader}>
            <span style={statusDot(ACCENT.emerald.icon)} />
            <h4 style={tileTitle}>Data Protection Gate</h4>
          </div>
          <div style={innerTabBar}>
            {['native', 'external'].map(t => (
              <button key={t} style={innerTabStyle(dpgTab === t)} onClick={() => setDpgTab(t)}>
                {t === 'native' ? 'Native' : 'External'}
              </button>
            ))}
          </div>
          <div style={tileBody}>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 10, marginBottom: 16 }}>
              {[
                { label: 'Scans', val: (computed.totalExec * 2).toLocaleString(), accent: ACCENT.sky },
                { label: 'Mutations', val: computed.dpgMutations, accent: ACCENT.amber },
                { label: 'Blocked', val: envelopes.filter(e => { const r = e.dpgInboundResult || e.dpgOutboundResult; return r && r.blocked; }).length, accent: ACCENT.rose },
              ].map((m, i) => (
                <div key={i} style={{ background: S[50], borderRadius: 10, padding: '12px 14px', textAlign: 'center', border: `1px solid ${S[100]}` }}>
                  <div style={{ fontSize: 10, color: S[400], fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.06em' }}>{m.label}</div>
                  <div style={{ fontSize: 22, fontWeight: 800, color: m.accent.icon, marginTop: 4, letterSpacing: '-0.02em' }}>{m.val}</div>
                </div>
              ))}
            </div>
            <div style={{ fontSize: 10, color: S[500], fontWeight: 700, marginBottom: 8, textTransform: 'uppercase', letterSpacing: '0.06em' }}>
              Top Detection Categories
            </div>
            <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
              <Badge text="PII" bg={ACCENT.amber.bg} color={ACCENT.amber.text} border={ACCENT.amber.border} />
              <Badge text="API Keys" bg={ACCENT.sky.bg} color={ACCENT.sky.text} border={ACCENT.sky.border} />
              <Badge text="Financial" bg={ACCENT.violet.bg} color={ACCENT.violet.text} border={ACCENT.violet.border} />
              <Badge text="Other" bg={S[100]} color={S[600]} border={S[200]} />
            </div>
          </div>
        </Card>

        {/* ── AGENTS REQUIRING ATTENTION (span 6) ── */}
        <Card style={{ gridColumn: 'span 6' }}>
          <div style={tileHeader}>
            <span style={statusDot(attentionAgents.length > 0 ? ACCENT.amber.icon : ACCENT.emerald.icon)} />
            <h4 style={tileTitle}>Agents Requiring Attention</h4>
          </div>
          <div style={{ padding: '14px 20px' }}>
            {attentionAgents.length === 0 ? (
              <div style={{ textAlign: 'center', padding: 24, color: ACCENT.emerald.icon }}>
                <CheckCircle size={28} strokeWidth={1.5} />
                <div style={{ fontSize: 13, fontWeight: 700, marginTop: 8, color: ACCENT.emerald.text }}>All agents healthy</div>
                <div style={{ fontSize: 11, color: S[400], marginTop: 4 }}>No agents require immediate attention</div>
              </div>
            ) : (
              <table style={miniTableStyle}>
                <thead>
                  <tr>
                    <th style={miniTh}>Agent</th>
                    <th style={miniTh}>Type</th>
                    <th style={miniTh}>Issue</th>
                    <th style={miniTh}>Action</th>
                  </tr>
                </thead>
                <tbody>
                  {attentionAgents.map((a, i) => (
                    <tr key={i} className="gov-row">
                      <td style={miniTd}><strong style={{ color: S[800] }}>{a.name}</strong></td>
                      <td style={miniTd}>
                        <Badge
                          text={a.type}
                          bg={a.type === 'External' ? ACCENT.amber.bg : ACCENT.sky.bg}
                          color={a.type === 'External' ? ACCENT.amber.text : ACCENT.sky.text}
                          border={a.type === 'External' ? ACCENT.amber.border : ACCENT.sky.border}
                        />
                      </td>
                      <td style={miniTd}><span style={{ color: ACCENT.rose.text, fontSize: 12 }}>{a.issue}</span></td>
                      <td style={miniTd}>
                        <button style={drillLink} onClick={() => navigate('/gel-agents')}>
                          Review <ChevronRight size={11} />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </Card>

        {/* ── LIVE ACTIVITY FEED (span 6) ── */}
        <Card style={{ gridColumn: 'span 6' }}>
          <div style={tileHeader}>
            <h4 style={tileTitle}>Live Activity Feed</h4>
            <span style={{ width: 6, height: 6, borderRadius: '50%', background: ACCENT.emerald.icon,
                           animation: 'govPulse 2s infinite', marginLeft: 4 }} />
          </div>
          <div style={tileBody}>
            <div style={{ position: 'relative', paddingLeft: 22 }}>
              <div style={{ position: 'absolute', left: 7, top: 0, bottom: 0, width: 2, background: S[200], borderRadius: 1 }} />
              {activityFeed.length === 0 ? (
                <div style={{ padding: 24, textAlign: 'center', color: S[400], fontSize: 12 }}>
                  No recent activity
                </div>
              ) : (
                activityFeed.map((evt, i) => {
                  const phaseAccent = evt.phase === 'COMPLETED' ? ACCENT.emerald
                    : evt.phase === 'BLOCKED' ? ACCENT.rose : ACCENT.amber;
                  return (
                    <div key={i} style={{ position: 'relative', paddingBottom: 14, animation: 'govSlideUp 0.3s ease', animationDelay: `${i * 0.05}s`, animationFillMode: 'backwards' }}>
                      <div style={{ position: 'absolute', left: -19, top: 4, width: 10, height: 10,
                                    borderRadius: '50%', border: `2px solid ${phaseAccent.icon}`, background: '#fff' }} />
                      <div style={{ fontSize: 10, color: S[400], fontFeatureSettings: "'tnum'" }}>
                        {evt.time ? new Date(evt.time).toLocaleTimeString() : '—'}
                      </div>
                      <div style={{ fontSize: 12, color: S[700], marginTop: 2, display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
                        <strong style={{ color: S[800] }}>{evt.agentId?.substring(0, 8) || 'agent'}…</strong>
                        <Badge text={evt.phase} bg={phaseAccent.bg} color={phaseAccent.text} border={phaseAccent.border} />
                        {evt.platformType && (
                          <Badge text="External" bg={ACCENT.amber.bg} color={ACCENT.amber.text} border={ACCENT.amber.border} />
                        )}
                      </div>
                    </div>
                  );
                })
              )}
            </div>
          </div>
        </Card>

        {/* ── ENVELOPE INTEGRITY (span 4) ── */}
        <Card style={{ gridColumn: 'span 4' }}>
          <div style={tileHeader}>
            <span style={statusDot(ACCENT.emerald.icon)} />
            <h4 style={tileTitle}>Envelope Integrity</h4>
          </div>
          <div style={tileBody}>
            <div style={{ textAlign: 'center', marginBottom: 14 }}>
              <div style={{
                width: 48, height: 48, borderRadius: 14, margin: '0 auto 10px',
                background: ACCENT.emerald.bg, border: `1px solid ${ACCENT.emerald.border}`,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
              }}>
                <CheckCircle2 size={22} color={ACCENT.emerald.icon} />
              </div>
              <div style={{ fontSize: 14, fontWeight: 700, color: ACCENT.emerald.text }}>Hash Chains Active</div>
              <div style={{ fontSize: 11, color: S[400], marginTop: 4, fontFeatureSettings: "'tnum'" }}>
                {computed.totalExec.toLocaleString()} envelopes tracked
              </div>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
              <div style={{ background: ACCENT.emerald.bg, borderRadius: 10, padding: 12, textAlign: 'center', border: `1px solid ${ACCENT.emerald.border}` }}>
                <div style={{ fontSize: 18, fontWeight: 800, color: ACCENT.emerald.icon }}>{computed.nativeAgentCount}</div>
                <div style={{ fontSize: 10, color: S[500], fontWeight: 600 }}>Native</div>
              </div>
              <div style={{ background: ACCENT.emerald.bg, borderRadius: 10, padding: 12, textAlign: 'center', border: `1px solid ${ACCENT.emerald.border}` }}>
                <div style={{ fontSize: 18, fontWeight: 800, color: ACCENT.emerald.icon }}>{computed.externalAgentCount}</div>
                <div style={{ fontSize: 10, color: S[500], fontWeight: 600 }}>External</div>
              </div>
            </div>
          </div>
        </Card>

        {/* ── EXTERNAL AGENT FRAMEWORKS (span 4) ── */}
        <Card style={{ gridColumn: 'span 4' }}>
          <div style={tileHeader}>
            <h4 style={tileTitle}>External Agent Frameworks</h4>
            <button style={drillLink} onClick={() => navigate('/gel-connectors')}>
              Manage <ChevronRight size={12} />
            </button>
          </div>
          <div style={tileBody}>
            <div style={{ display: 'flex', gap: 8, marginBottom: 14 }}>
              {[
                { label: 'Healthy', count: computed.healthyCon, accent: ACCENT.emerald },
                { label: 'Degraded', count: computed.degradedCon, accent: ACCENT.amber },
                { label: 'Offline', count: computed.offlineCon, accent: ACCENT.slate },
              ].map((s, i) => (
                <div key={i} style={{ flex: 1, background: s.accent.bg, borderRadius: 10, padding: '10px 12px', textAlign: 'center', border: `1px solid ${s.accent.border}` }}>
                  <div style={{ fontSize: 18, fontWeight: 800, color: s.accent.icon }}>{s.count}</div>
                  <div style={{ fontSize: 10, color: s.accent.text, fontWeight: 600 }}>{s.label}</div>
                </div>
              ))}
            </div>
            <div style={{ fontSize: 12 }}>
              {connectors.slice(0, 5).map((c, i) => (
                <div key={i} className="gov-row" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '8px 6px',
                                      borderBottom: i < Math.min(connectors.length, 5) - 1 ? `1px solid ${S[100]}` : 'none' }}>
                  <span style={{ color: S[700], fontWeight: 500 }}>{c.name || c.platformType || 'Connector'}</span>
                  <Badge
                    text={c.healthStatus || '—'}
                    bg={(c.healthStatus === 'HEALTHY' || c.healthStatus === 'UP') ? ACCENT.emerald.bg : ACCENT.amber.bg}
                    color={(c.healthStatus === 'HEALTHY' || c.healthStatus === 'UP') ? ACCENT.emerald.text : ACCENT.amber.text}
                    border={(c.healthStatus === 'HEALTHY' || c.healthStatus === 'UP') ? ACCENT.emerald.border : ACCENT.amber.border}
                  />
                </div>
              ))}
              {connectors.length === 0 && (
                <div style={{ textAlign: 'center', padding: 16, color: S[400], fontSize: 12 }}>No connectors configured</div>
              )}
            </div>
          </div>
        </Card>

        {/* ── GOVERNED GATEWAY (span 4) ── */}
        <Card style={{ gridColumn: 'span 4' }}>
          <div style={tileHeader}>
            <Key size={14} color={BRAND.primary} />
            <h4 style={tileTitle}>Protected Gateway</h4>
            <button style={drillLink} onClick={() => navigate('/gateway-dashboard')}>
              Dashboard <ChevronRight size={12} />
            </button>
          </div>
          <div style={tileBody}>
            <div style={{ display: 'flex', gap: 8, marginBottom: 14 }}>
              {[
                { label: 'Active Keys', count: computed.gwActive, accent: ACCENT.emerald },
                { label: 'Suspended', count: computed.gwSuspended, accent: ACCENT.rose },
                { label: 'Total', count: gatewayKeys.length, accent: ACCENT.slate },
              ].map((s, i) => (
                <div key={i} style={{ flex: 1, background: s.accent.bg, borderRadius: 10, padding: '10px 12px', textAlign: 'center', border: `1px solid ${s.accent.border}` }}>
                  <div style={{ fontSize: 18, fontWeight: 800, color: s.accent.icon }}>{s.count}</div>
                  <div style={{ fontSize: 10, color: S[400], fontWeight: 600 }}>{s.label}</div>
                </div>
              ))}
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, marginBottom: 10 }}>
              <div style={{ background: ACCENT.sky.bg, borderRadius: 10, padding: 12, textAlign: 'center', border: `1px solid ${ACCENT.sky.border}` }}>
                <div style={{ fontSize: 16, fontWeight: 800, color: ACCENT.sky.icon, fontFeatureSettings: "'tnum'" }}>{computed.gwTotalReqs.toLocaleString()}</div>
                <div style={{ fontSize: 10, color: S[400], fontWeight: 600 }}>Total Requests</div>
              </div>
              <div style={{
                background: computed.gwTotalBlocked > 0 ? ACCENT.rose.bg : ACCENT.emerald.bg,
                borderRadius: 10, padding: 12, textAlign: 'center',
                border: `1px solid ${computed.gwTotalBlocked > 0 ? ACCENT.rose.border : ACCENT.emerald.border}`,
              }}>
                <div style={{ fontSize: 16, fontWeight: 800, color: computed.gwTotalBlocked > 0 ? ACCENT.rose.icon : ACCENT.emerald.icon, fontFeatureSettings: "'tnum'" }}>{computed.gwTotalBlocked.toLocaleString()}</div>
                <div style={{ fontSize: 10, color: S[400], fontWeight: 600 }}>Blocked</div>
              </div>
            </div>
            <button
              className="gov-btn-ghost"
              onClick={() => navigate('/gateway-keys')}
              style={{
                ...btnBase, width: '100%', padding: '9px 14px', borderRadius: 8,
                background: BRAND.light, color: BRAND.primary, fontSize: 11, fontWeight: 700,
                border: `1px solid ${BRAND.soft || S[200]}`, justifyContent: 'center',
              }}>
              <Key size={12} /> Manage API Keys
            </button>
          </div>
        </Card>

        {/* ── PER-AGENT COMPLIANCE DRILL-DOWN (span 12) ── */}
        <Card style={{ gridColumn: 'span 12' }}>
          <div style={tileHeader}>
            <span style={statusDot(BRAND.primary)} />
            <h4 style={tileTitle}>Per-Agent Compliance Drill-Down</h4>
            <button style={drillLink} onClick={() => navigate('/compliance')}>
              Full Compliance Center <ChevronRight size={12} />
            </button>
          </div>
          <div style={{ padding: '14px 20px 20px' }}>
            {/* Agent selector + Framework filter row */}
            <div style={{ display: 'flex', gap: 12, alignItems: 'flex-end', marginBottom: 16, flexWrap: 'wrap' }}>
              <div style={{ flex: '1 1 240px' }}>
                <label style={{ fontSize: 10, fontWeight: 700, color: S[400], textTransform: 'uppercase',
                                letterSpacing: '0.06em', display: 'block', marginBottom: 4 }}>Select Agent</label>
                <select
                  style={{ ...inputBase, cursor: 'pointer' }}
                  value={selectedCompAgent || ''}
                  onChange={e => { setSelectedCompAgent(e.target.value || null); setCompAgentSummary(null); setCompAgentControls([]); }}
                >
                  <option value="">— Choose an agent —</option>
                  {complianceAgents.map(a => (
                    <option key={a.id} value={a.id}>{a.name} ({a.type})</option>
                  ))}
                </select>
              </div>
              <div style={{ flex: '1 1 200px' }}>
                <label style={{ fontSize: 10, fontWeight: 700, color: S[400], textTransform: 'uppercase',
                                letterSpacing: '0.06em', display: 'block', marginBottom: 4 }}>Framework Filter</label>
                <select
                  style={{ ...inputBase, cursor: 'pointer' }}
                  value={compSelectedFw || ''}
                  onChange={e => setCompSelectedFw(e.target.value || null)}
                >
                  <option value="">All Frameworks</option>
                  {compFrameworks.map(fw => (
                    <option key={fw.id} value={fw.id}>{fw.code || fw.name}</option>
                  ))}
                </select>
              </div>
              <div style={{ flex: '1 1 200px' }}>
                <label style={{ fontSize: 10, fontWeight: 700, color: S[400], textTransform: 'uppercase',
                                letterSpacing: '0.06em', display: 'block', marginBottom: 4 }}>Search Controls</label>
                <SearchInput value={compControlSearch} onChange={setCompControlSearch} placeholder="Filter controls..." maxWidth={999} />
              </div>
              {selectedCompAgent && (
                <PrimaryButton
                  icon={RefreshCw}
                  onClick={() => api.ccRecalculateCompliance(selectedCompAgent).then(() => loadAgentCompliance(selectedCompAgent, compSelectedFw))}
                >
                  Recalculate
                </PrimaryButton>
              )}
            </div>

            {!selectedCompAgent ? (
              <div style={{ textAlign: 'center', padding: 36, color: S[400] }}>
                <div style={{
                  width: 52, height: 52, borderRadius: 14, margin: '0 auto 14px',
                  background: BRAND.gradientSubtle, border: `1px solid ${S[200]}`,
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                }}>
                  <Shield size={24} color={S[400]} strokeWidth={1.5} />
                </div>
                <div style={{ fontSize: 13, fontWeight: 600, color: S[600] }}>Select an agent above to view its compliance breakdown</div>
              </div>
            ) : compLoading ? (
              <div style={{ textAlign: 'center', padding: 36, color: S[400] }}>
                <RefreshCw size={18} style={{ animation: 'govSpin 1s linear infinite' }} />
                <div style={{ fontSize: 12, marginTop: 8, color: S[500] }}>Loading compliance data…</div>
              </div>
            ) : (
              <>
                {/* Summary cards */}
                {compAgentSummary && (
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: 10, marginBottom: 16 }}>
                    {[
                      { val: `${(compAgentSummary.score || 0).toFixed(1)}%`, label: 'Compliance Score',
                        accent: compAgentSummary.score >= 80 ? ACCENT.emerald : compAgentSummary.score >= 60 ? ACCENT.amber : ACCENT.rose },
                      { val: compAgentSummary.passing || 0, label: 'Passing Controls', accent: ACCENT.emerald },
                      { val: compAgentSummary.failing || 0, label: 'Failing Controls', accent: ACCENT.rose },
                      { val: compAgentSummary.totalControls || 0, label: 'Total Controls', accent: ACCENT.slate },
                      { val: compAgentSummary.openRemediations || 0, label: 'Open Remediations', accent: ACCENT.amber },
                    ].map((m, i) => (
                      <div key={i} style={{ background: m.accent.bg, borderRadius: 10, padding: '12px 14px', textAlign: 'center', border: `1px solid ${m.accent.border}` }}>
                        <div style={{ fontSize: 22, fontWeight: 800, color: m.accent.icon, letterSpacing: '-0.02em' }}>{m.val}</div>
                        <div style={{ fontSize: 10, color: S[500], fontWeight: 600, marginTop: 2 }}>{m.label}</div>
                      </div>
                    ))}
                  </div>
                )}

                {/* Compliance progress bar */}
                {compAgentSummary && (
                  <div style={{ marginBottom: 16 }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 11, color: S[500], marginBottom: 6 }}>
                      <span style={{ fontWeight: 600 }}>Required vs. Current Compliance</span>
                      <span style={{ fontFeatureSettings: "'tnum'" }}>{compAgentSummary.passing || 0} / {compAgentSummary.totalControls || 0} controls passing</span>
                    </div>
                    <div style={{ height: 8, background: S[100], borderRadius: 4, overflow: 'hidden' }}>
                      <div style={{
                        height: '100%', borderRadius: 4, transition: 'width 0.4s ease',
                        width: compAgentSummary.totalControls > 0
                          ? `${(compAgentSummary.passing / compAgentSummary.totalControls * 100).toFixed(1)}%`
                          : '0%',
                        background: compAgentSummary.score >= 80
                          ? `linear-gradient(90deg, ${ACCENT.emerald.icon}, #34d399)`
                          : compAgentSummary.score >= 60
                            ? `linear-gradient(90deg, ${ACCENT.amber.icon}, #fbbf24)`
                            : `linear-gradient(90deg, ${ACCENT.rose.icon}, #fb7185)`,
                      }} />
                    </div>
                  </div>
                )}

                {/* Control-level breakdown table */}
                {compAgentControls.length > 0 ? (
                  <div style={{ maxHeight: 280, overflowY: 'auto', border: `1px solid ${S[200]}`, borderRadius: 10 }}>
                    <table style={{ ...miniTableStyle, margin: 0 }}>
                      <thead>
                        <tr>
                          <th style={{ ...miniTh, position: 'sticky', top: 0, background: S[50], zIndex: 1 }}>Control Code</th>
                          <th style={{ ...miniTh, position: 'sticky', top: 0, background: S[50], zIndex: 1 }}>Status</th>
                          <th style={{ ...miniTh, position: 'sticky', top: 0, background: S[50], zIndex: 1 }}>Score</th>
                          <th style={{ ...miniTh, position: 'sticky', top: 0, background: S[50], zIndex: 1 }}>Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {compAgentControls
                          .filter(c => !compControlSearch || (c.code || '').toLowerCase().includes(compControlSearch.toLowerCase()))
                          .map((ctrl, i) => {
                            const ctrlAccent = ctrl.status === 'PASSING' ? ACCENT.emerald
                              : ctrl.status === 'FAILING' ? ACCENT.rose : ACCENT.amber;
                            return (
                              <tr key={i} className="gov-row">
                                <td style={miniTd}><strong style={{ fontSize: 12, color: S[800] }}>{ctrl.code || ctrl.controlId?.substring(0, 12)}</strong></td>
                                <td style={miniTd}>
                                  <Badge text={ctrl.status || 'PENDING'} bg={ctrlAccent.bg} color={ctrlAccent.text} border={ctrlAccent.border} />
                                </td>
                                <td style={miniTd}>
                                  <span style={{ fontWeight: 700, color: ctrlAccent.icon, fontFeatureSettings: "'tnum'" }}>
                                    {ctrl.score != null ? `${parseFloat(ctrl.score).toFixed(0)}%` : '—'}
                                  </span>
                                </td>
                                <td style={miniTd}>
                                  <button style={drillLink}
                                    onClick={() => navigate(`/compliance?tab=attestations&control=${ctrl.controlId}`)}>
                                    Attest <ChevronRight size={11} />
                                  </button>
                                </td>
                              </tr>
                            );
                          })}
                      </tbody>
                    </table>
                  </div>
                ) : compAgentSummary ? (
                  <div style={{ textAlign: 'center', padding: 24, color: S[400], fontSize: 12,
                                background: S[50], borderRadius: 10, border: `1px solid ${S[200]}` }}>
                    No control-level data available. Run a compliance recalculation to populate control scores.
                  </div>
                ) : null}
              </>
            )}
          </div>
        </Card>

        {/* ── BOARD REPORT PREVIEW (span 4) ── */}
        <Card style={{ gridColumn: 'span 4' }}>
          <div style={tileHeader}>
            <FileText size={14} color={S[500]} />
            <h4 style={tileTitle}>Board Report Preview</h4>
          </div>
          <div style={tileBody}>
            <div style={{ background: S[50], borderRadius: 10, padding: 16,
                          border: `1px dashed ${S[300]}`, fontSize: 12,
                          color: S[600], lineHeight: 1.7 }}>
              <div style={{ fontSize: 13, fontWeight: 700, marginBottom: 10, color: S[800], display: 'flex', alignItems: 'center', gap: 6 }}>
                <FileText size={14} color={BRAND.primary} />
                Agent Protection Summary
              </div>
              {[
                { k: 'Fleet', v: `${computed.nativeAgentCount} native + ${computed.externalAgentCount} external agents` },
                { k: 'Risk', v: `${computed.riskScore}/100 — ${riskLabel}` },
                { k: 'Compliance', v: `${computed.complianceRate}% policy adherence` },
                { k: 'Integrity', v: `${computed.totalExec.toLocaleString()} envelopes tracked` },
                { k: 'DPG', v: `${computed.dpgMutations} mutations, ${computed.blocked} blocks` },
                { k: 'Gateway', v: `${computed.gwActive} active keys, ${computed.gwTotalReqs.toLocaleString()} requests` },
              ].map((r, i) => (
                <div key={i} style={{ marginBottom: 4 }}>
                  <strong style={{ color: S[700] }}>{r.k}:</strong> {r.v}
                </div>
              ))}
              <PrimaryButton icon={Download} onClick={() => {}}>
                Generate Full PDF Report
              </PrimaryButton>
            </div>
          </div>
        </Card>

      </div>{/* end mosaic */}
    </V2PageShell>
  );
}
